import axios, { AxiosError, AxiosRequestConfig, AxiosResponse } from 'axios';
import { getAuth } from './auth';
import { setBearerAuthorization } from './bearerAuthorization';
import { notifySessionExpired } from './sessionExpiry';

// Derive default DOS base URL from current origin so the app works behind a reverse proxy.
const defaultDataObjectBase =
  process.env.REACT_APP_DATA_OBJECT_API_URL ||
  `${window.location.origin.replace(/\/$/, '')}/api/data-object`;

// Separate axios instance for Data Object Service
const axiosDataObject = axios.create({
  baseURL: defaultDataObjectBase,
  timeout: 30000,
});

axiosDataObject.interceptors.request.use(
  async (config) => {
    try {
      const auth = getAuth();
      const token = await auth.getToken();

      setBearerAuthorization(config.headers, token);
      // Do not send X-Tenant-ID: strict tenant identity is derived from validated JWT claims.
    } catch (e) {
      console.warn('Auth not available for dataObjectApi');
    }
    return config;
  },
  (error) => Promise.reject(error)
);

axiosDataObject.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const original = error.config as AxiosRequestConfig & { _retry?: boolean };
    if (error.response?.status === 401 && !original._retry) {
      try {
        await getAuth().refresh();
        const token = await getAuth().getToken();

        // Create a new config object with updated headers to avoid mutating readonly properties
        const retryConfig: AxiosRequestConfig & { _retry: boolean } = {
          ...original,
          _retry: true,
          headers: {
            ...original.headers,
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
        };

        return axiosDataObject(retryConfig);
      } catch (e) {
        // Prompt re-authentication instead of a silent logout mid-session.
        notifySessionExpired();
      }
    }
    return Promise.reject(error);
  }
);

export const dataObjectApi = {
  get: async <T>(url: string, config?: AxiosRequestConfig): Promise<T> => {
    const res: AxiosResponse<T> = await axiosDataObject.get(url, config);
    return res.data;
  },
  post: async <T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> => {
    const res: AxiosResponse<T> = await axiosDataObject.post(url, data, config);
    return res.data;
  },
  put: async <T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> => {
    const res: AxiosResponse<T> = await axiosDataObject.put(url, data, config);
    return res.data;
  },
  delete: async <T>(url: string, config?: AxiosRequestConfig): Promise<T> => {
    const res: AxiosResponse<T> = await axiosDataObject.delete(url, config);
    return res.data;
  },
};

export type DataObjectDto = {
  id: string;
  name: string;
  description?: string;
  fileType: 'CSV' | 'EXCEL' | 'JSON' | 'PARQUET';
  version: number;
  createdAt: string;
  updatedAt: string;
};

export type DataObjectInsightsDto = {
  id: string;
  name: string;
  fileType: 'CSV' | 'EXCEL' | 'JSON' | 'PARQUET';
  version: number;
  uploadedAt: string;
  updatedAt: string;
  lastUsedAt?: string | null;
  fileSizeBytes?: number | null;
  rowCount?: number | null;
  compatibleDataContracts?: string[];
};

export type DataObjectVersionDto = {
  version: number;
  fileType: 'CSV' | 'EXCEL' | 'JSON' | 'PARQUET';
  createdAt: string;
  createdBy?: string | null;
  hasSchema: boolean;
};
